/* istanbul ignore file */
import React from 'react';
import groupBy from 'lodash/groupBy';
import { useLazyQuery } from 'apollo-client-ppc';
import { AppOffering, PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { EntitlementIcon, renderPromotionOption, withApollo } from '@css-xp/base';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { Field } from '@atlaskit/form';
import Button from '@atlaskit/button';
import { N90 } from '@atlaskit/theme/colors';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { ProxyGraphClients } from '@atlassian/temp-client-utils';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { PageInfo, Promotion } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import { EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { ManageAppsPromocode } from './manage-apps-promocode';
import { getPromotionType, renderPromotionBenefits, getBenefitsLength } from './utils/partner-promotion-utils';
import { PromocodeDetails } from './manage-apps-promocode/manage-apps-promocode';
import { PartnerProductAppSearchList } from './product-app-search/product-app-search-list';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import { fetchSearchBasedAppsForProductsInPPCQuery } from './queries/sxp-queries/fetch-seach-based-apps';
import {
  APP_PAGINATION_SIZE,
  JIRA_RELATIONSHIP_OFFERING_KEY,
} from './hooks/use-process-line-items-for-order-intent-estimate';
import { AvailableAppForProduct } from './types';
import './partner-quote-catalog-select-apps.scss';

type AvailableAppsForProduct = {
  edges?: AvailableAppForProduct[];
  nodes?: AvailableAppForProduct[];
  pageInfo: PageInfo;
  totalCount?: number;
};

export type PartnerQuoteCatalogSelectAppsProps = {
  product: PartnerProductCatalogueItem;
  setSelectedApps: (selectedApps: any) => void;
  selectedApps: any;
  index: number;
  jiraFamilyItems?: PartnerProductCatalogueItem[];
  nonJiraFamilyItems?: PartnerProductCatalogueItem[];
  offeringId: string;
  maxUserCount: number;
  pricingPlanCycle?: PricingPlanCycle;
  isJPDAsJiraFamilyEnabled?: boolean;
};

const PartnerQuoteCatalogSelectAppsImpl = ({
  product,
  setSelectedApps,
  selectedApps,
  offeringId,
  maxUserCount,
  isJPDAsJiraFamilyEnabled,
}: PartnerQuoteCatalogSelectAppsProps) => {
  const { setProductTrial, removeProductTrial, removeAppsPromotionId } = usePartnerCatalogueContext();

  const [appDetails, setAppDetails] = React.useState<any>([]);
  const [selectedAppIds, setSelectedAppIds] = React.useState<any>([]);
  const [closeSearchDropDown, setCloseSearchDropDown] = React.useState<boolean>(true);
  const [appCursor, setAppCursor] = React.useState<string>('');
  const [availableProductsApps, setAvailableProductsApps] = React.useState<AvailableAppsForProduct>();
  const [appSearchText, setAppSearchText] = React.useState<string>('');
  const [appSearchInputValue, setAppSearchInputValue] = React.useState<string>('');

  // promocode related states
  const [appsPromocodeDetails, setAppsPromocodeDetails] = React.useState<Map<string, PromocodeDetails>>(new Map());
  const [appsPromocodeFieldEnable, setAppsPromocodeFieldEnable] = React.useState<Map<string, boolean>>(new Map());

  const [fetchAvailableAppData, { data: availableProductAppsList, loading: appsDataLoading }] = useLazyQuery(
    fetchSearchBasedAppsForProductsInPPCQuery
  );

  const validOfferingIds = React.useMemo(() => {
    if (isProductBelongsToJiraFamily(defaultTo(get(product, 'id'), ''), { isJPDAsJiraFamilyEnabled })) {
      return [JIRA_RELATIONSHIP_OFFERING_KEY];
    }
    return [offeringId];
  }, [product, offeringId]);

  React.useEffect(() => {
    if (appSearchText) {
      fetchAvailableAppData({
        variables: {
          searchText: appSearchText,
          ids: validOfferingIds,
          pageParams: {
            size: APP_PAGINATION_SIZE,
            cursor: appCursor,
          },
        },
      });
    }
  }, [product, appSearchText, appCursor, offeringId]);

  const availableProductAppsData = React.useMemo(() => {
    return defaultTo(get(availableProductAppsList, 'availableAppsOnSearchBased'), []);
  }, [availableProductAppsList]);

  React.useEffect(() => {
    if (availableProductAppsData?.nodes?.length) {
      const allAvailableProductApps = [
        ...(appCursor && defaultTo(availableProductsApps?.nodes, [])),
        ...availableProductAppsData?.nodes,
      ];

      const groupByAvailableAppsProductId = groupBy(allAvailableProductApps, 'id');
      /**
       * Here we are grouping the apps based on the id and then we are filtering the apps based on the standard offering
       * if standard offering is not present then we are taking the first app from the list
       * This is done to avoid duplicate apps in the list
       */
      const allAppsWithStandardOrDefaultOfferings = Object.values(groupByAvailableAppsProductId).map(
        (availableApps) => {
          const availableStandardOfferingApp = availableApps.find(
            (item) => item?.offeringName?.toUpperCase() === EntitlementEdition.STANDARD
          );
          return availableStandardOfferingApp || availableApps[0];
        }
      );

      // to make unique array
      const uniqueAvailableProductApps = [
        ...new Map(allAppsWithStandardOrDefaultOfferings.map((item) => [item['id'], item])).values(),
      ];
      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [...uniqueAvailableProductApps],
      });
    } else if (availableProductAppsData?.nodes) {
      // setting empty array when apps not present in the data
      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [],
      });
      setAppCursor('');
      setAppSearchText('');
    }
  }, [availableProductAppsData]);

  React.useEffect(() => {
    // updates the price so that we can show total price on page
    appDetails.forEach((app: AppOffering) => {
      for (let appItem of defaultTo(selectedApps[product?.id], [])) {
        if (get(appItem, 'id') === get(app, 'id')) {
          appItem = { ...appItem, price: defaultTo(get(app, 'price'), 0), maxUserCount };
        }
      }
    });
    setSelectedApps({ ...selectedApps });
  }, [appDetails]);

  const addApp = (item: string) => {
    let newApp: AvailableAppForProduct[];
    if (selectedAppIds?.includes(item)) {
      return;
    } else {
      const selectedEnt = availableProductsApps?.nodes?.find((app: AvailableAppForProduct) => get(app, 'id') === item);
      if (selectedEnt?.productKey) {
        setProductTrial(selectedEnt.productKey, selectedEnt?.trialLengthInDays ?? null);
      }

      newApp = [...appDetails, selectedEnt];
      setSelectedAppIds((prevIds: any) => [...prevIds, item]);
      setAppDetails(newApp);
      setSelectedApps({ ...selectedApps, [product?.id]: newApp });
    }
  };

  const addPromocodeDetails = (productId: string, promocodeDetail: PromocodeDetails) => {
    const newMap = new Map(appsPromocodeDetails);
    newMap.set(productId, promocodeDetail);
    setAppsPromocodeDetails(newMap);
  };

  const enablePromocodeDetails = (productId: string) => {
    // enabling promocode for new app
    const newMap = new Map(appsPromocodeFieldEnable);
    setAppsPromocodeFieldEnable(newMap.set(productId, true));
  };

  const removePromocodeDetails = (productId: string) => {
    appsPromocodeDetails.delete(productId);
    setAppsPromocodeDetails(new Map(appsPromocodeDetails));

    removeAppsPromotionId(productId);
    // not enabling promocode field once it got removed
    const newMap = new Map(appsPromocodeFieldEnable);
    setAppsPromocodeFieldEnable(newMap.set(productId, false));
  };

  const handleSearchInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAppSearchInputValue(value);
    if (!value) {
      setAppSearchText?.('');
      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [],
      });
    }
  };
  return (
    <div className="search-product-apps-container">
      <Field name={'apps.' + get(product, 'id')} defaultValue={appDetails}>
        {({ fieldProps }) => (
          <div className="product-app-field">
            <Textfield
              role="search"
              className="search-app-field"
              aria-label={'search-new-product'}
              placeholder={'Search apps'}
              testId="product-app-search"
              elemBeforeInput={<SearchIcon label="" size="small" />}
              value={appSearchInputValue}
              onChange={handleSearchInputOnChange}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setAppSearchText?.(e.currentTarget.value);
                  setCloseSearchDropDown?.(false);
                }
              }}
            />
            {appsDataLoading && <div className="app-search-loader"> {<LoadingIndicator />}</div>}
            {availableProductsApps && !appsDataLoading && !closeSearchDropDown && appSearchText && (
              <PartnerProductAppSearchList
                productAppList={availableProductsApps}
                fieldProps={fieldProps}
                onClick={addApp}
                onClose={setCloseSearchDropDown}
                setCursor={setAppCursor}
              />
            )}
          </div>
        )}
      </Field>
      <>
        {appDetails?.map((item: any, index: number) => {
          const appPromocodeDetail = appsPromocodeDetails.get(item?.id);
          const isPromocodeFieldEnable = appsPromocodeFieldEnable.get(item?.id);
          const isPromocodeValid = appPromocodeDetail && Object.keys(appPromocodeDetail)?.length > 0;
          const promotionTypeContent = getPromotionType(appPromocodeDetail as Promotion);
          const promotionBenefitsContent = renderPromotionBenefits(appPromocodeDetail as Promotion);
          const benefitLength = getBenefitsLength(appPromocodeDetail as Promotion);
          const appProductId = get(item, 'id');
          const promotionTooltipContent = [promotionTypeContent, promotionBenefitsContent].join(' ');

          return (
            <div className="selected-apps-container" key={`selected-app-${index}`}>
              <div key={item?.id} className="app-option" data-testid="selected-apps-list">
                <div className="selected-product-apps" key={index}>
                  <EntitlementIcon
                    productId={appProductId}
                    logoId={get(item, 'logoId')}
                    className="avatar large"
                    isCloud
                  />
                  <div className="selected-app-name pl-8">{get(item, 'name')}</div>
                  <div
                    className="remove-app"
                    onClick={() => {
                      const appToRemove = appDetails[index];

                      const updatedAppList = selectedAppIds?.filter((ele: AppOffering) => {
                        return appToRemove.id !== ele;
                      });

                      const finalAppList = appDetails?.filter((ele: AppOffering) => {
                        return appToRemove?.id !== ele?.id;
                      });

                      if (appToRemove?.productKey) {
                        removeProductTrial(appToRemove.productKey);
                      }
                      setAppDetails && setAppDetails(finalAppList);
                      setSelectedAppIds && setSelectedAppIds(updatedAppList);
                    }}
                    data-testid={`remove-app-${appProductId}`}
                  >
                    <CrossIcon size="small" label="Remove app" />
                  </div>
                </div>
              </div>

              {
                // here we need to hide the promocode input field
                !isPromocodeValid &&
                  (isPromocodeFieldEnable ? (
                    <div className="app-promocode-item">
                      <ManageAppsPromocode
                        productId={appProductId}
                        addPromocodeDetails={addPromocodeDetails}
                        removePromocodeDetails={removePromocodeDetails}
                        isPromotionDetailHidden={true}
                      />
                    </div>
                  ) : (
                    <div className="app-promocode-item">
                      <Button
                        appearance="link"
                        spacing="none"
                        testId={'add-promo-code-' + appProductId}
                        className="ml-16"
                        onClick={() => {
                          enablePromocodeDetails(appProductId);
                        }}
                      >
                        Add vendor promotion
                      </Button>
                    </div>
                  ))
              }

              {isPromocodeValid && (
                <>
                  <div className="app-promocode-detail">
                    <p className="app-promocode-detail-title">
                      {appPromocodeDetail?.title + (benefitLength ? ` with ${benefitLength}` : '') ?? ''}
                      <Tooltip content={promotionTooltipContent} tag="span">
                        <InfoIcon label="Info" size="small" />
                      </Tooltip>
                    </p>
                  </div>
                  <div className="app-promocode-text">
                    {appPromocodeDetail?.promocode}
                    <Button
                      appearance="subtle"
                      className="remove-app-promocode pl-12"
                      onClick={() => removePromocodeDetails(appProductId)}
                      testId="remove-app-icon"
                      iconBefore={<SelectClearIcon primaryColor={N90} label="" size="small" />}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </>
    </div>
  );
};

export const PartnerQuoteCatalogSelectApps = withApollo(PartnerQuoteCatalogSelectAppsImpl, ProxyGraphClients.SXP);
